import { useEffect, useMemo, useState } from 'react'

import { createLocalAgentClient, type LocalAgentClient } from '@krp/client-runtime'
import type { EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareStatus, HardwareTabId } from '@krp/platform-ui'

import { createHardwareController, toSnapshot, type ControllerDeps, type HardwareModel } from './hardwareController'
import { AGENT_INSTALLER_URL, AGENT_MIN_API_LEVEL, AGENT_RECOMMENDED_VERSION, DRIVER_LINKS } from './hardwareLinks'

/** Ссылки окна из настроек приложения (`hardwareLinks.ts`): агент их не отдаёт и не хранит. */
export const DEFAULT_HARDWARE_LINKS: HardwareLinks = {
  agentInstaller: AGENT_INSTALLER_URL,
  drivers: DRIVER_LINKS,
  recommendedAgentVersion: AGENT_RECOMMENDED_VERSION,
}

export interface UseHardwareOptions {
  /** Открытый таб из адреса: от него зависит, где срабатывает автоскан. `null` — таб ещё не выбран. */
  tab: HardwareTabId | null
  /** Подставной клиент агента (тесты). */
  client?: LocalAgentClient | undefined
  links?: HardwareLinks
  siteOrigin?: string
  minApiLevel?: number
  clock?: () => number
  openUrl?: (url: string) => void
  copyText?: (text: string) => Promise<void>
}

export interface HardwareState {
  snapshot: EquipmentSnapshot
  links: HardwareLinks
  status: HardwareStatus
  actions: HardwareActions
  /** Первый просмотр закончен. */
  observed: boolean
}

/**
 * Состояние окна «Оборудование» для React: хук держит контроллер (`hardwareController.ts`) и показывает его модель.
 * Опрос идёт, пока окно открыто; контроллер один на жизнь окна и от перерисовок не пересоздаётся.
 */
export function useHardware(options: UseHardwareOptions): HardwareState {
  const client = useMemo(() => options.client ?? createLocalAgentClient(), [options.client])
  const links = options.links ?? DEFAULT_HARDWARE_LINKS
  const siteOrigin = options.siteOrigin ?? (typeof window === 'undefined' ? '' : window.location.origin)
  const minApiLevel = options.minApiLevel ?? AGENT_MIN_API_LEVEL
  const [model, setModel] = useState<HardwareModel | null>(null)
  const controller = useMemo(() => {
    const deps: ControllerDeps = {
      client,
      links,
      siteOrigin,
      minApiLevel,
      onChange: (next) => setModel(next),
    }
    return createHardwareController(deps)
  }, [client, links, siteOrigin, minApiLevel])

  // Часы и обработчики подставляются при каждой перерисовке (до запуска опроса): контроллер из-за них не пересоздаётся.
  const { clock, openUrl, copyText } = options
  useEffect(() => {
    controller.configure({
      ...(clock !== undefined ? { clock } : {}),
      ...(openUrl !== undefined ? { openUrl } : {}),
      ...(copyText !== undefined ? { copyText } : {}),
    })
  }, [controller, clock, openUrl, copyText])

  useEffect(() => {
    controller.start()
    return () => controller.stop()
  }, [controller])

  const tab = options.tab
  useEffect(() => {
    controller.setTab(tab)
  }, [controller, tab])

  const current = model ?? controller.getModel()
  const snapshot = useMemo(() => toSnapshot(current), [current])
  const windows = typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent)
  const status = useMemo<HardwareStatus>(
    () => ({ busy: current.busy, checking: current.checking, notice: current.notice, windows }),
    [current.busy, current.checking, current.notice, windows],
  )
  return { snapshot, links, status, actions: controller.actions, observed: current.observed }
}
